import { Box, Button } from '@mui/material';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    cancelSourcingOperation,
    editOrderItemSourcingActions,
    getCanSave,
    getIsLoading,
    getZeroTotalQuantityError,
    saveOrderItemSourcing,
} from 'features/EditOrderItemSourcing';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { Loader } from 'shared/ui/Loader/Loader';

interface SaveCancelButtonsProps {
    onCancel?: () => void;
    onSave?: () => void;
}

export const SaveCancelButtons = memo(
    ({ onCancel, onSave }: SaveCancelButtonsProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();

        const canSave = useSelector(getCanSave);
        const isLoading = useSelector(getIsLoading);
        const zeroTotalQuantityError = useSelector(getZeroTotalQuantityError);

        const handleSave = useCallback(async () => {
            if (isLoading) return;

            if (zeroTotalQuantityError) {
                dispatch(editOrderItemSourcingActions.flagTotalQuantityError());
                return;
            }

            if (!canSave) return;

            await dispatch(saveOrderItemSourcing()).unwrap();
            onSave?.();
        }, [canSave, isLoading, zeroTotalQuantityError, dispatch, onSave]);

        const handleCancel = useCallback(() => {
            // Cancel any active sourcing operations
            dispatch(cancelSourcingOperation());
            onCancel?.();
        }, [dispatch, onCancel]);

        return (
            <Box display="flex" gap="12px" justifyContent="flex-end">
                <Button
                    onClick={handleCancel}
                    disabled={isLoading}
                    variant="outlined"
                    sx={{
                        minWidth: '80px',
                    }}
                >
                    {t('Cancel')}
                </Button>
                <Button
                    data-testclass="order-item-sourcing-save"
                    onClick={handleSave}
                    className={!canSave || isLoading ? 'Mui-disabled' : ''}
                    disabled={
                        isLoading || (!canSave && !zeroTotalQuantityError)
                    }
                    variant="contained"
                    startIcon={isLoading && <Loader color="inherit" />}
                    sx={{
                        minWidth: '80px',
                        pointerEvents: 'auto',
                        cursor:
                            !canSave || isLoading ? 'not-allowed' : 'pointer',
                        '&.Mui-disabled': {
                            pointerEvents: 'auto',
                            cursor: 'not-allowed',
                        },
                    }}
                >
                    {isLoading ? t('Saving...') : t('Save')}
                </Button>
            </Box>
        );
    },
);
